import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealApi } from '../services/pipelineApi';
import type { DealCreate, DealUpdate, ListDealsParams } from '../types';
import { pipelineKeys } from './usePipelines';
import { backlinksKeys } from '../../master_data/contacts/hooks/useBacklinks';
import { useLastUsedPipeline } from './useLastUsedPipeline';

export const dealKeys = {
  all: ['deals'] as const,
  list: (params?: ListDealsParams) => [...dealKeys.all, 'list', params] as const,
  detail: (id: string) => [...dealKeys.all, 'detail', id] as const,
  activities: (id: string) => [...dealKeys.all, 'activities', id] as const,
};

/**
 * Clean undefined values from params for consistent query key comparison.
 * React Query does deep comparison, but { key: undefined } vs {} can cause issues.
 */
function cleanParams(params?: ListDealsParams): ListDealsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListDealsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useDeals(params?: ListDealsParams) {
  // Memoize cleaned params to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => cleanParams(params), [
    params,
    params?.search,
    params?.pipeline_id,
    params?.stage_id,
    params?.organization_id,
    params?.owner_id,
    params?.team_filter,
    params?.stage_type,
    params?.archived_only,
    params?.include_archived,
    params?.custom_fields,
    params?.admin_scope,
    params?.limit,
    params?.offset,
  ]);

  return useQuery({
    queryKey: dealKeys.list(stableParams),
    queryFn: () => dealApi.list(stableParams),
  });
}

export function useDeal(id: string) {
  return useQuery({
    queryKey: dealKeys.detail(id),
    queryFn: () => dealApi.get(id),
    enabled: !!id,
  });
}

export function useDealActivities(id: string) {
  return useQuery({
    queryKey: dealKeys.activities(id),
    queryFn: () => dealApi.getActivities(id),
    enabled: !!id,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  const { rememberPipeline } = useLastUsedPipeline();
  return useMutation({
    mutationFn: (data: DealCreate) => dealApi.create(data),
    onSuccess: (deal) => {
      // Remember the chosen pipeline so the next New Deal pre-selects it.
      rememberPipeline(deal.pipeline_id);
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DealUpdate }) => dealApi.update(id, data),
    onSuccess: (deal, { id }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

export function useMoveDealToStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, stageId, extra }: { dealId: string; stageId: string; fromPipelineId?: string; extra?: { loss_reason?: string; competitor?: string; actual_close_date?: string } }) =>
      dealApi.moveToStage(dealId, stageId, extra),
    onSuccess: (deal, { fromPipelineId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(deal.id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
      // Cross-pipeline move: the source pipeline's summary/totals also changed.
      if (fromPipelineId && fromPipelineId !== deal.pipeline_id) {
        queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(fromPipelineId) });
      }
    },
  });
}

export function useAddContactToDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealId,
      contactId,
      role,
      isPrimary,
    }: {
      dealId: string;
      contactId: string;
      role?: string;
      isPrimary?: boolean;
    }) => dealApi.addContact(dealId, contactId, role, isPrimary),
    onSuccess: (_, { dealId, contactId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: backlinksKeys.contactDeals(contactId) });
      queryClient.invalidateQueries({ queryKey: backlinksKeys.contactCounts(contactId) });
    },
  });
}

export function useUpdateDealContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealId,
      contactId,
      role,
      isPrimary,
    }: {
      dealId: string;
      contactId: string;
      role?: string;
      isPrimary?: boolean;
    }) => dealApi.updateContact(dealId, contactId, { role, is_primary: isPrimary }),
    onSuccess: (_, { dealId, contactId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: backlinksKeys.contactDeals(contactId) });
    },
  });
}

export function useRemoveContactFromDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, contactId }: { dealId: string; contactId: string }) =>
      dealApi.removeContact(dealId, contactId),
    onSuccess: (_, { dealId, contactId }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: backlinksKeys.contactDeals(contactId) });
      queryClient.invalidateQueries({ queryKey: backlinksKeys.contactCounts(contactId) });
    },
  });
}

export function useDuplicateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.duplicate(id),
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useArchiveDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.archive(id),
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(deal.id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useUnarchiveDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealApi.unarchive(id),
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(deal.id) });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.summary(deal.pipeline_id) });
    },
  });
}

export function useBulkDeleteDeals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => dealApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

export function useBulkRestoreDeals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => dealApi.bulkRestore(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

export function useBulkArchiveDeals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => dealApi.bulkArchive(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}
